import React, { useState } from 'react'
import { Icon, Button, Popover, Dialog } from '@equinor/eds-core-react'
import { close as closeIcon } from '@equinor/eds-icons'
import styled from 'styled-components'
import { baseFont, ClosedStyledLabel, EdsStyledLabel } from '@/styles/layout'
import { SortItem } from '@/core/models/types'
import LabelWithRadioButtons from './LabelWithRadioButtons'
import { SORT_OPTIONS } from '../dashboardTable/TableBar'

interface SortDialogProps {
    showMenu: boolean
    onClose: () => void
    onSortChange: (
        type: 'tag' | 'installationDate' | 'device' | 'sensorStatus' | 'batteryCapacity' | 'signalStrength',
        selected: string
    ) => void
    sortTag: SortItem
    sortInstallationDate: SortItem
    sortDevice: SortItem
    sortSensorStatus: SortItem
    sortBatteryCapacity: SortItem
    sortSignalStrength: SortItem
    anchor: HTMLElement | null
}

const SortDialog: React.FC<SortDialogProps> = ({
    showMenu,
    onClose,
    onSortChange,
    sortTag,
    sortInstallationDate,
    sortDevice,
    sortSensorStatus,
    sortBatteryCapacity,
    sortSignalStrength,
    anchor,
}) => {
    const [windowWidth] = useState(window.innerWidth)

    if (!showMenu) return null

    const tagOptions = [
        { label: 'A-Z', value: sortTag?.first ?? SORT_OPTIONS.tag.first },
        { label: 'Z-A', value: sortTag?.second ?? SORT_OPTIONS.tag.second },
    ]

    const installationDateOptions = [
        { label: 'Newest', value: sortInstallationDate?.first ?? SORT_OPTIONS.installationDate.first },
        { label: 'Oldest', value: sortInstallationDate?.second ?? SORT_OPTIONS.installationDate.second },
    ]

    const deviceOptions = [
        { label: 'A-Z', value: sortDevice?.first ?? SORT_OPTIONS.device.first },
        { label: 'Z-A', value: sortDevice?.second ?? SORT_OPTIONS.device.first },
    ]

    const sensorStatusOptions = [
        { label: 'A-Z', value: sortSensorStatus?.first ?? SORT_OPTIONS.sensorStatus.first },
        { label: 'Z-A', value: sortSensorStatus?.second ?? SORT_OPTIONS.sensorStatus.first },
    ]

    const batteryCapacityOptions = [
        { label: 'Low', value: sortBatteryCapacity?.first ?? SORT_OPTIONS.batteryCapacity.first },
        { label: 'High', value: sortBatteryCapacity?.second ?? SORT_OPTIONS.batteryCapacity.first },
    ]

    const signalStrengthOptions = [
        { label: 'Low', value: sortSignalStrength?.first ?? SORT_OPTIONS.signalStrength.first },
        { label: 'High', value: sortSignalStrength?.second ?? SORT_OPTIONS.signalStrength.first },
    ]

    const sortTitle = 'Sort by'

    const renderContent = () => (
        <StyledContainer>
            <LabelWithRadioButtons
                label="Tag"
                options={tagOptions}
                selectedValue={sortTag.selected ?? null}
                onChange={(value) => onSortChange('tag', value)}
            />
            <LabelWithRadioButtons
                label="Installation date"
                options={installationDateOptions}
                selectedValue={sortInstallationDate.selected ?? null}
                onChange={(value) => onSortChange('installationDate', value)}
            />
            <LabelWithRadioButtons
                label="Device"
                options={deviceOptions}
                selectedValue={sortDevice?.selected ?? null}
                onChange={(value) => onSortChange('device', value)}
            />
            <LabelWithRadioButtons
                label="Sensor status"
                options={sensorStatusOptions}
                selectedValue={sortSensorStatus?.selected ?? null}
                onChange={(value) => onSortChange('sensorStatus', value)}
            />
            <LabelWithRadioButtons
                label="Battery capacity"
                options={batteryCapacityOptions}
                selectedValue={sortBatteryCapacity?.selected ?? null}
                onChange={(value) => onSortChange('batteryCapacity', value)}
            />
            <LabelWithRadioButtons
                label="Signal strength"
                options={signalStrengthOptions}
                selectedValue={sortSignalStrength?.selected ?? null}
                onChange={(value) => onSortChange('signalStrength', value)}
            />
        </StyledContainer>
    )

    if (anchor == null) {
        return (
            <SortContainer open={showMenu} onClose={onClose} isDismissable>
                <button /* Invisible button to avoid auto focus upon entering dialog container*/
                    tabIndex={-1}
                    style={{
                        position: 'absolute',
                        width: 1,
                        height: 1,
                        overflow: 'hidden',
                        opacity: 0,
                    }}
                />
                <MenuHeader>
                    <EdsStyledLabel group="heading" variant={windowWidth <= 1024 ? 'h4' : 'h5'}>
                        {sortTitle}
                    </EdsStyledLabel>
                    <CloseButton as="Link" onClick={onClose}>
                        {windowWidth <= 1024 && <ClosedStyledLabel>Close</ClosedStyledLabel>}
                        <StyledIcon data={closeIcon} size={24} />
                    </CloseButton>
                </MenuHeader>
                <Content>{renderContent()}</Content>
            </SortContainer>
        )
    }

    return (
        <Popover open={showMenu} anchorEl={anchor} onClose={onClose}>
            <Popover.Header>
                <Popover.Title>{sortTitle}</Popover.Title>
                <Button
                    style={{ height: '32px', width: '32px' }}
                    variant="ghost_icon"
                    aria-label="Close popover"
                    onClick={onClose}
                >
                    <Icon data={closeIcon} size={24} />
                </Button>
            </Popover.Header>
            <Popover.Content>{renderContent()}</Popover.Content>
        </Popover>
    )
}

const SortContainer = styled(Dialog)`
    display: flex;
    flex-direction: column;
    width: 500px;
    height: auto;
    overflow-y: auto;
    overflow-x: hidden;

    @media (max-width: 1024px) {
        position: fixed;
        left: 0;
        right: 0;
        bottom: 82px;
        top: auto;
        height: auto;
        max-height: calc(100vh - 64px - 75px - 64px - 16px - 16px - 16px - 8px);
        width: 100%;
        background: #ffffff;
        box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.15);
        transform: none;
        border-radius: 0;
    }
`

const MenuHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-left: 16px;

    button {
        background: none;
        border: none;
        cursor: pointer;
    }

    @media (max-width: 1024px) {
        color: #1d1d1d;
        margin-left: 0;
        margin-right: 0;
        padding-left: 16px;
        padding-right: 16px;
        padding-bottom: 12px;
        border-bottom: 1px solid #dcdcdc;
    }
`

const Content = styled.div`
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 1rem;
    flex-grow: 1;
    margin-bottom: 16px;
`

const CloseButton = styled(Button)`
    ${baseFont}
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 16px;
    text-decoration: none;
    background: none;
    border: none;
    cursor: pointer;
    color: #007079;

    &:hover {
        background: #eaeaea;
    }

    @media (max-width: 1024px) {
        color: #1f6369;

        &:hover {
            background: #f5f5f5;
        }
    }
`

const StyledIcon = styled(Icon)`
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #007079;

    @media (max-width: 1024px) {
        color: #1f6369;

        &:hover {
            background: #f5f5f5;
            color: #1f6369;
        }
    }
`

const StyledContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 95%;
    margin-left: 16px;
    gap: 16px;
`

export default SortDialog
